import { msalInstance } from '../MsalConfig';
import { User, UserWithToken } from '../Types/user';
import { API_URL, enrichedFetch } from './api';

export const login = (email?: string, password?: string): Promise<User> => {
  const options: RequestInit = {
    method: 'GET',
  };

  if (email && password) {
    options.body = JSON.stringify({
      email,
      password,
    });
    options.method = 'POST';
    options.headers = {
      'Content-Type': 'application/json',
    };
  }
  return enrichedFetch(`${API_URL}/user/login`, options) as Promise<User>;
};

export const loginAzureAD = (): Promise<UserWithToken> => {
  return enrichedFetch(`${API_URL}/user/login/azuread`) as Promise<UserWithToken>;
};

export const getAzureGraphToken = (): Promise<string> => {
  return enrichedFetch(`${API_URL}/user/graphToken`) as Promise<string>;
};

export const logoutUserService = (): Promise<User> => {
  return enrichedFetch(`${API_URL}/user/logout`, {
    method: 'POST',
  }) as Promise<User>;
};

/**
 * Logs out the current user by clearing session and local storage,
 * and calling the logout service. Displays an error notification if logout fails.
 */
export const logoutUser = async () => {
  sessionStorage.clear();
  localStorage.clear();
  await logoutUserService();
};

/**
 * Attempts to fetch and store authentication tokens for the application and Microsoft Graph API.
 *
 * This function tries to silently acquire tokens using MSAL. If silent acquisition fails,
 * it falls back to a popup method, and finally to a redirect method. If all methods fail,
 * the user is logged out.
 *
 * - Stores the application token in `localStorage` under the key `'token'`.
 * - Stores the Microsoft Graph token in `localStorage` under the key `'graphToken'`.
 *
 * @throws Will log the user out if all token acquisition methods fail.
 */
export const fetchTokens = async () => {
  try {
    const token = await msalInstance.acquireTokenSilent({
      scopes: [`api://${import.meta.env.VITE_AZUREAD_CLIENT_ID}/dev/app.dev`],
      account: msalInstance.getAllAccounts()[0],
    });
    const graphToken = await msalInstance.acquireTokenSilent({
      scopes: ['Sites.Read.All'],
      account: msalInstance.getAllAccounts()[0],
    });
    localStorage.setItem('token', token.accessToken);
    localStorage.setItem('graphToken', graphToken.accessToken);
  } catch (error) {
    try {
      const token = await msalInstance.acquireTokenPopup({
        scopes: [`api://${import.meta.env.VITE_AZUREAD_CLIENT_ID}/dev/app.dev`],
        account: msalInstance.getAllAccounts()[0],
      });
      const graphToken = await msalInstance.acquireTokenPopup({
        scopes: ['Sites.Read.All'],
        account: msalInstance.getAllAccounts()[0],
      });
      localStorage.setItem('token', token.accessToken);
      localStorage.setItem('graphToken', graphToken.accessToken);
    } catch (popupError) {
      try {
        await msalInstance.acquireTokenRedirect({
          scopes: [`api://${import.meta.env.VITE_AZUREAD_CLIENT_ID}/dev/app.dev`],
          account: msalInstance.getAllAccounts()[0],
        });
        await msalInstance.acquireTokenRedirect({
          scopes: ['Sites.Read.All'],
          account: msalInstance.getAllAccounts()[0],
        });
      } catch (redirectError) {
        logoutUser();
      }
    }
  }
};
